import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, Send, MapPin, Clock, MessageCircle } from 'lucide-react';
import { contactInfo } from '../data/mockData';
import { useLanguage } from '../context/LanguageContext';

const ContactSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const [formData, setFormData] = useState({ phone: '', model: '', problem: '' });
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setFormData({ phone: '', model: '', problem: '' });
  };

  const inputStyle = (name) => ({
    background: 'rgba(30, 64, 175, 0.3)',
    border: `1.5px solid ${focusedField === name ? 'rgba(147, 197, 253, 0.6)' : 'rgba(147, 197, 253, 0.3)'}`,
    borderRadius: '0.8rem',
    boxShadow: focusedField === name ? '0 0 15px rgba(147, 197, 253, 0.2)' : 'none',
  });

  return (
    <section id="contact" ref={ref} className="py-16 sm:py-28 relative noise-overlay" style={{ background: 'linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)' }}>
      <div className="gold-line mb-10 sm:mb-16" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-10 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white mb-3 sm:mb-4">
            <span className="gold-text-shimmer">{t('contact.headerTitle')}</span>
          </h2>
          <p className="text-blue-100 text-sm sm:text-lg max-w-2xl mx-auto px-2">
            {t('contact.headerSubtitle')}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-6 sm:gap-10">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div
              className="p-6 sm:p-10 relative overflow-hidden"
              style={{
                borderRadius: '1.5rem',
                background: 'linear-gradient(160deg, rgba(37, 99, 235, 0.45) 0%, rgba(30, 64, 175, 0.3) 100%)',
                border: '1px solid rgba(96, 165, 250, 0.2)',
              }}
            >
              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6 relative z-10">
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm text-blue-50 mb-2 font-medium">{t('contact.form.phoneLabel')}</label>
                    <input
                      type="tel"
                      placeholder={t('contact.form.phonePlaceholder')}
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="w-full px-4 py-3 sm:py-3.5 text-white text-sm placeholder-gray-600 outline-none transition-all duration-300"
                      style={inputStyle('phone')}
                      onFocus={() => setFocusedField('phone')}
                      onBlur={() => setFocusedField(null)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm text-blue-50 mb-2 font-medium">{t('contact.form.modelLabel')}</label>
                    <input
                      type="text"
                      placeholder={t('contact.form.modelPlaceholder')}
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="w-full px-4 py-3 sm:py-3.5 text-white text-sm placeholder-gray-600 outline-none transition-all duration-300"
                      style={inputStyle('model')}
                      onFocus={() => setFocusedField('model')}
                      onBlur={() => setFocusedField(null)}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm text-blue-50 mb-2 font-medium">{t('contact.form.issueLabel')}</label>
                  <textarea
                    placeholder={t('contact.form.issuePlaceholder')}
                    rows={3}
                    value={formData.problem}
                    onChange={(e) => setFormData({ ...formData, problem: e.target.value })}
                    className="w-full px-4 py-3 sm:py-3.5 text-white text-sm placeholder-gray-600 outline-none resize-none transition-all duration-300"
                    style={inputStyle('problem')}
                    onFocus={() => setFocusedField('problem')}
                    onBlur={() => setFocusedField(null)}
                  />
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 py-3.5 sm:py-4 text-sm sm:text-base font-bold text-black relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #c9a84c 0%, #e8d48b 50%, #c9a84c 100%)',
                    boxShadow: '0 6px 25px rgba(96, 165, 250, 0.2)',
                    borderRadius: '0.8rem',
                  }}
                >
                  <div className="absolute inset-0 opacity-30" style={{ background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.5) 50%, transparent 60%)', backgroundSize: '200% 100%', animation: 'shimmer 3s ease-in-out infinite' }} />
                  <Send size={16} className="relative z-10" />
                  <span className="relative z-10">{submitted ? t('contact.form.successMessage') : t('contact.form.submit')}</span>
                </motion.button>
              </form>
            </div>
          </motion.div>

          {/* Info side */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="lg:col-span-5 space-y-4 sm:space-y-6"
          >
            {/* WhatsApp */}
            <div
              className="p-5 sm:p-7"
              style={{
                borderRadius: '1.2rem',
                background: 'rgba(30, 64, 175, 0.3)',
                borderRight: '3px solid rgba(96, 165, 250, 0.4)',
              }}
            >
              <h3 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4">{t('footer.businessName')} — {t('footer.ownerName')}</h3>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.95 }}
                className="w-full flex items-center justify-center gap-2 py-3 text-sm font-bold"
                style={{ background: '#25D366', color: 'white', borderRadius: '0.8rem' }}
                onClick={() => window.open(contactInfo.whatsapp)}
              >
                <MessageCircle size={18} />
                WhatsApp
              </motion.button>
            </div>

            {/* Contact Info */}
            <div
              className="p-5 sm:p-7 space-y-5"
              style={{
                borderRadius: '1.2rem',
                background: 'rgba(14, 30, 56, 0.4)',
                border: '1px solid rgba(96, 165, 250, 0.15)',
              }}
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <Phone size={20} className="flex-shrink-0" style={{ color: '#6ee7b7' }} />
                <div>
                  <p className="text-xs text-blue-200 mb-1">{t('contact.info.phone')}</p>
                  <p className="text-white text-sm sm:text-base font-bold">{contactInfo.phone}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <Clock size={20} className="flex-shrink-0" style={{ color: '#60a5fa' }} />
                <div>
                  <p className="text-xs text-blue-200 mb-1">{t('contact.info.hours')}</p>
                  <p className="text-white text-sm sm:text-base font-bold">{t('contact.info.hoursValue')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <MapPin size={20} className="flex-shrink-0" style={{ color: '#c9a84c' }} />
                <div>
                  <p className="text-xs text-blue-200 mb-1">{t('contact.info.social')}</p>
                  <p className="text-white text-sm sm:text-base leading-relaxed">{t('footer.serviceArea')}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-24" style={{ background: 'linear-gradient(to top, #1e40af, transparent)' }} />
    </section>
  );
};

export default ContactSection;
